/**
 * Employer Capabilities and Programs Table
 * Shows employer benefit capabilities and which programs are gated by each capability
 */

import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Gift, Lock, Unlock, RefreshCw, AlertCircle, Briefcase } from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { useAppTranslation } from '@/contexts/TranslationContext';

interface Capability {
  id: number;
  organization_id: number;
  capability_name: string;
  enabled: boolean;
  config: Record<string, any>;
}

interface BenefitProgram {
  id: number;
  employer_id: number;
  program_code: string;
  program_name: string;
  program_type: string;
  program_category: string;
  status: string;
  required_capability?: string;
}

interface Employer {
  id: number;
  company_name: string;
  cbo_id: number;
}

// Define which capabilities gate which program types/categories
const CAPABILITY_PROGRAM_MAPPING: Record<string, { types: string[]; categories: string[]; description: string }> = {
  employer_platform: {
    types: [],
    categories: [],
    description: 'Core platform - required for all programs'
  },
  employer_employee_portal: {
    types: ['SELF_SERVICE'],
    categories: ['WELLNESS', 'HEALTH'],
    description: 'Employee self-service programs'
  },
  employer_navigation_sessions: {
    types: ['NAVIGATION', 'CARE_COORDINATION'],
    categories: ['NAVIGATION', 'SUPPORT'],
    description: 'Navigation and care coordination programs'
  },
  employer_recommendations: {
    types: ['RECOMMENDATION'],
    categories: [],
    description: 'AI-powered program recommendations'
  },
  employer_nudge_engine: {
    types: ['ENGAGEMENT'],
    categories: ['ENGAGEMENT', 'BEHAVIORAL'],
    description: 'Automated engagement programs'
  },
  employer_leave_disability: {
    types: ['LEAVE', 'DISABILITY', 'ADA', 'FMLA'],
    categories: ['LEAVE', 'DISABILITY', 'ACCOMMODATION'],
    description: 'Leave and disability programs'
  },
  employer_analytics: {
    types: [],
    categories: [],
    description: 'Analytics and reporting'
  },
  employer_hris_integration: {
    types: [],
    categories: [],
    description: 'HRIS integration for roster sync'
  },
  employer_workspace_messaging: {
    types: ['MESSAGING'],
    categories: ['COMMUNICATION'],
    description: 'Slack/Teams messaging programs'
  },
  employer_protected_mode: {
    types: ['SENSITIVE', 'ANONYMOUS'],
    categories: ['MENTAL_HEALTH', 'SUBSTANCE_ABUSE', 'DOMESTIC'],
    description: 'Protected/anonymous mode programs'
  },
};

interface EmployerCapabilitiesTableProps {
  organizationId: number;
  organizationName: string;
}

export function EmployerCapabilitiesTable({
  organizationId,
  organizationName
}: EmployerCapabilitiesTableProps) {
  const { t } = useAppTranslation();
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [programs, setPrograms] = useState<BenefitProgram[]>([]);
  const [employer, setEmployer] = useState<Employer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [organizationId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load capabilities for this org
      const capsResponse = await apiClient.get(`/api/organizations/${organizationId}/capabilities`, {
        headers: { 'X-CBO-ID': organizationId.toString() }
      });
      setCapabilities(capsResponse.data);

      // Load employer linked to this org
      const employerResponse = await apiClient.get('/api/employers', {
        params: { cbo_id: organizationId },
        headers: { 'X-CBO-ID': organizationId.toString() }
      });

      if (employerResponse.data.employers?.length > 0) {
        const emp = employerResponse.data.employers[0];
        setEmployer(emp);

        // Load benefit programs for this employer
        const programsResponse = await apiClient.get(`/api/employers/${emp.id}/benefit-programs`, {
          headers: { 'X-CBO-ID': organizationId.toString() }
        });
        setPrograms(programsResponse.data);
      }
    } catch (err: any) {
      console.error('Failed to load employer capabilities:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const isCapabilityEnabled = (capabilityName: string): boolean => {
    const cap = capabilities.find(c => c.capability_name === capabilityName);
    return cap?.enabled || false;
  };

  const getCapabilityStatus = (capabilityName: string): 'enabled' | 'disabled' | 'not_configured' => {
    const cap = capabilities.find(c => c.capability_name === capabilityName);
    if (!cap) return 'not_configured';
    return cap.enabled ? 'enabled' : 'disabled';
  };

  const getProgramsForCapability = (capabilityName: string): BenefitProgram[] => {
    const mapping = CAPABILITY_PROGRAM_MAPPING[capabilityName];
    if (!mapping) return [];

    return programs.filter(p => {
      const typeMatch = mapping.types.length === 0 || mapping.types.includes(p.program_type?.toUpperCase() || '');
      const categoryMatch = mapping.categories.length === 0 || mapping.categories.includes(p.program_category?.toUpperCase() || '');
      return typeMatch || categoryMatch;
    });
  };

  const employerCapabilities = Object.keys(CAPABILITY_PROGRAM_MAPPING);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-blue-600 animate-spin mr-2" />
        <span className="text-gray-600">{t('admin.loading_employer_capabilities', 'Loading employer capabilities...')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
          <span className="text-red-800">{error}</span>
        </div>
        <button
          onClick={loadData}
          className="mt-3 text-sm text-red-600 hover:text-red-800 font-medium"
        >
          {t('admin.try_again', 'Try Again')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Briefcase className="w-6 h-6 text-indigo-600" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t('admin.employer_capabilities_programs', 'Employer Capabilities & Programs')}
            </h3>
            <p className="text-sm text-gray-500">
              {employer ? employer.company_name : organizationName}
            </p>
          </div>
        </div>
        <button
          onClick={loadData}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title={t('admin.refresh', 'Refresh')}
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-green-700">
              {t('admin.enabled_capabilities', 'Enabled')}
            </span>
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-green-800 mt-1">
            {employerCapabilities.filter(c => getCapabilityStatus(c) === 'enabled').length}
          </p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-red-700">
              {t('admin.disabled_capabilities', 'Disabled')}
            </span>
            <XCircle className="w-5 h-5 text-red-600" />
          </div>
          <p className="text-2xl font-bold text-red-800 mt-1">
            {employerCapabilities.filter(c => getCapabilityStatus(c) === 'disabled').length}
          </p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-700">
              {t('admin.total_programs', 'Programs')}
            </span>
            <Gift className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-blue-800 mt-1">{programs.length}</p>
        </div>
      </div>

      {/* Capabilities Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('admin.capability', 'Capability')}
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('admin.status', 'Status')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('admin.gated_programs', 'Gated Programs')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {employerCapabilities.map((capName) => {
              const status = getCapabilityStatus(capName);
              const mapping = CAPABILITY_PROGRAM_MAPPING[capName];
              const gatedPrograms = getProgramsForCapability(capName);

              return (
                <tr key={capName} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {capName.replace(/employer_/g, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{mapping.description}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    {status === 'enabled' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <Unlock className="w-3 h-3 mr-1" />
                        {t('admin.enabled', 'Enabled')}
                      </span>
                    ) : status === 'disabled' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <Lock className="w-3 h-3 mr-1" />
                        {t('admin.disabled', 'Disabled')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {t('admin.not_configured', 'Not Configured')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {mapping.types.length === 0 && mapping.categories.length === 0 ? (
                      <span className="text-xs text-gray-400 italic">
                        {capName === 'employer_platform' ? t('admin.all_programs', 'All programs') : t('admin.na', 'N/A')}
                      </span>
                    ) : gatedPrograms.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {gatedPrograms.slice(0, 3).map((p) => (
                          <span
                            key={p.id}
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              status === 'enabled'
                                ? 'bg-green-50 text-green-700 border border-green-200'
                                : 'bg-gray-100 text-gray-500 border border-gray-200'
                            }`}
                          >
                            {p.program_name}
                          </span>
                        ))}
                        {gatedPrograms.length > 3 && (
                          <span className="text-xs text-gray-500">
                            +{gatedPrograms.length - 3} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">
                        {t('admin.no_programs_configured', 'No programs configured')}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Programs List */}
      {programs.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h4 className="text-sm font-medium text-gray-900">
              {t('admin.all_benefit_programs', 'All Benefit Programs')} ({programs.length})
            </h4>
          </div>
          <div className="divide-y divide-gray-200 max-h-64 overflow-y-auto">
            {programs.map((program) => (
              <div key={program.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                <div>
                  <p className="text-sm font-medium text-gray-900">{program.program_name}</p>
                  <p className="text-xs text-gray-500">
                    {program.program_type} • {program.program_category}
                  </p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  program.status === 'ACTIVE'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {program.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No employer linked message */}
      {!employer && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
            <span className="text-yellow-800">
              {t('admin.no_employer_linked', 'No employer is linked to this organization. Link an employer to see benefit programs.')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmployerCapabilitiesTable;
